import { useState } from 'react';
import { useNavigate } from 'react-router';
import { getBidApplicationPopupKey } from '@tempmee/awards';
import { BidTrophy, Button, Column, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { BestInDentalV2 } from '~/constants/ff.posthog';
import { useGetBidHistory } from '~/domain/BestInDental';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useUserSelector } from '~/hooks/useUserSelector';
import { routes } from '~/routes';
import { BID_DIALOG_STYLE } from '../shared';

const POPUP_DESCRIPTION =
  'GoTu leadership has nominated your office for Best in Dental, an honor reserved for a select group of standout practices. Submit your application to be considered.';

/**
 * Show-once nudge for a nominated + apply-eligible office to submit its
 * Best-in-Dental application (GOTU-5642). App.js mounts it only when the
 * v2 flag is on (mutually exclusive with the v1 popups); it renders `null`
 * unless the office has an eligible nomination and the per-user×cycle
 * localStorage key (`getBidApplicationPopupKey`) hasn't already been set.
 *
 * All hooks run unconditionally before the gating checks so the early
 * returns below never change hook call order across renders.
 */
export const BidApplicationPopup = () => {
  const navigate = useNavigate();
  const isFlagOn = useFeatureFlag(BestInDentalV2);
  const { items } = useGetBidHistory(1);
  const user = useUserSelector();
  // Tracks the key dismissed *this session* so hiding the popup doesn't
  // depend on `localStorage.getItem` reflecting the just-written value on
  // the very next render (true for real browsers, but keeps the component
  // correct even where that read-after-write isn't guaranteed).
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);

  const eligibleItem = items.find(
    (item) => item.membershipStatus === 'nominee' && item.applyEligible,
  );

  if (!isFlagOn || !eligibleItem || !user?.id) {
    return null;
  }

  const storageKey = getBidApplicationPopupKey(user.id, eligibleItem.cycleId);

  if (dismissedKey === storageKey || localStorage.getItem(storageKey) === 'true') {
    return null;
  }

  const dismiss = () => {
    localStorage.setItem(storageKey, 'true');
    setDismissedKey(storageKey);
  };

  const handleSubmitApplication = () => {
    dismiss();
    navigate(routes.bestInDentalApplication.replace(':cycleId', eligibleItem.cycleId));
  };

  return (
    <Dialog open onClose={dismiss} size="small" style={BID_DIALOG_STYLE} data-cy="bid-application-popup">
      {/* `pb-0` (via twMerge) cancels DialogTitle's own `p-4` bottom padding so
          the trophy badge below flows right up into the header row instead of
          leaving a full 16px gap under the close button — matches Figma. */}
      <DialogTitle onClose={dismiss} className="justify-end pb-0" />
      <DialogContent>
        <Column align="center" gap={4}>
          <BidTrophy size="lg" data-cy="bid-trophy-badge" />
          <Typography variant="subtitle1" bold color={colors.text.primary} textAlign="center">
            Your Office Was Nominated
          </Typography>
          <Typography color={colors.text.secondary} textAlign="center">
            {POPUP_DESCRIPTION}
          </Typography>
        </Column>
      </DialogContent>
      <DialogActions className="justify-center">
        <Button onClick={handleSubmitApplication} data-cy="bid-application-popup-submit">
          Submit Application
        </Button>
      </DialogActions>
    </Dialog>
  );
};
